// Charts (SPEC §8, §14). Colours are CSS variables, so light/dark follow the theme. Every chart
// cross-filters on click (a second click clears) and offers a table view.
import { useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export type Datum = { key: string; label: string; value: number; color: string; note?: string };

const axis = { fontSize: 11, fill: "var(--muted)" };
const tooltipStyle = {
  contentStyle: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: 6, fontSize: 12, color: "var(--ink)" },
  itemStyle: { color: "var(--ink)" },
  labelStyle: { color: "var(--muted)", fontWeight: 600 },
  cursor: { fill: "var(--hover)" },
};

export function ChartCard({
  title,
  caption,
  children,
  table,
  action,
  className = "",
}: {
  title: string;
  caption?: string;
  children: ReactNode;
  table?: { head: string[]; rows: (string | number)[][] };
  action?: ReactNode;
  className?: string;
}) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section className={`card p-3 md:p-4 flex flex-col min-w-0 ${className}`} aria-label={title}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-[14px] font-bold">{title}</h3>
          {caption && <p className="text-[11px] text-muted">{caption}</p>}
        </div>
        {action}
        {table && (
          <button className="text-[11.5px] text-brand whitespace-nowrap" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>
            {asTable ? "Chart" : "Table"}
          </button>
        )}
      </div>
      {asTable && table ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] tnum">
            <thead>
              <tr>
                {table.head.map((h, i) => (
                  <th key={h} className={`label py-1 ${i ? "text-right" : "text-left"}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i} className="border-t border-line">
                  {r.map((c, j) => (
                    <td key={j} className={`py-1 ${j ? "text-right" : ""}`}>
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        children
      )}
    </section>
  );
}

/**
 * Horizontal bars, one per category, coloured by identity (stage, bucket). Clicking a bar toggles
 * it as a filter; unselected bars dim while a selection exists, but all values stay visible.
 */
export function HBars({
  data,
  selected = [],
  onSelect,
  format = (n) => String(n),
  height,
}: {
  data: Datum[];
  selected?: string[];
  onSelect?: (key: string) => void;
  format?: (n: number) => string;
  height?: number;
}) {
  const h = height ?? Math.max(120, data.length * 30 + 10);
  return (
    <div style={{ height: h }}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 4, right: 48, top: 0, bottom: 0 }} barCategoryGap={4}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="label"
            width={150}
            tick={axis}
            axisLine={false}
            tickLine={false}
            // Long names (suppliers, accounts) are cut to one line; the tooltip and table keep them whole.
            tickFormatter={(v: string) => (v.length > 26 ? `${v.slice(0, 25)}…` : v)}
          />
          <Tooltip
            {...tooltipStyle}
            labelFormatter={(l: string) => l}
            formatter={(v: number, _n, p) => [`${format(v)}${p.payload.note ? ` · ${p.payload.note}` : ""}`, ""]}
            separator=""
          />
          <Bar
            dataKey="value"
            radius={[0, 4, 4, 0]}
            onClick={(d: { key?: string }) => d.key && onSelect?.(d.key)}
            cursor={onSelect ? "pointer" : undefined}
            label={{ position: "right", fontSize: 11, fill: "var(--ink-2)", formatter: (v: number) => format(v) }}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.key} fill={d.color} fillOpacity={selected.length && !selected.includes(d.key) ? 0.3 : 1} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * One tile per pipeline stage: the count large in the stage colour, the stage name under it, an
 * optional note (median age) below. Clicking a tile toggles it as a filter, as HBars does.
 */
export function StageCards({
  data,
  selected = [],
  onSelect,
  format = (n) => String(n),
}: {
  data: Datum[];
  selected?: string[];
  onSelect?: (key: string) => void;
  format?: (n: number) => string;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-2">
      {data.map((d) => {
        const on = selected.includes(d.key);
        return (
          <button
            key={d.key}
            type="button"
            onClick={() => onSelect?.(d.key)}
            disabled={!onSelect}
            aria-pressed={on}
            title={d.note ? `${d.label} · ${d.note}` : d.label}
            className={`text-left rounded border bg-[var(--card)] px-3 py-2 transition-opacity hover:bg-hover ${on ? "border-brand" : "border-line"}`}
            style={{ opacity: selected.length && !on ? 0.45 : 1, boxShadow: on ? "inset 0 0 0 1px var(--brand)" : undefined }}
          >
            <div className="text-[28px] leading-none font-light tnum" style={{ color: d.color }}>
              {format(d.value)}
            </div>
            <div className="mt-1.5 text-[12px] text-ink2 leading-tight">{d.label}</div>
            {d.note && <div className="text-[10.5px] text-muted mt-0.5">{d.note}</div>}
          </button>
        );
      })}
    </div>
  );
}

/** Vertical columns for ordered buckets (aging, cycle time); optional target marker. */
export function Columns({
  data,
  selected = [],
  onSelect,
  height = 200,
  target,
  format = (n) => String(n),
}: {
  data: Datum[];
  selected?: string[];
  onSelect?: (key: string) => void;
  height?: number;
  target?: { x: string; label: string };
  format?: (n: number) => string;
}) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: 0, right: 8, top: 16, bottom: 0 }} barCategoryGap="18%">
          <CartesianGrid vertical={false} stroke="var(--line)" />
          <XAxis dataKey="label" tick={axis} axisLine={{ stroke: "var(--line)" }} tickLine={false} interval={0} />
          <YAxis tick={axis} axisLine={false} tickLine={false} width={44} tickFormatter={(v: number) => format(v)} />
          <Tooltip {...tooltipStyle} formatter={(v: number) => [format(v), ""]} separator="" />
          {target && <ReferenceLine x={target.x} stroke="var(--ink-2)" strokeDasharray="4 3" label={{ value: target.label, position: "top", fontSize: 11, fill: "var(--ink-2)" }} />}
          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            onClick={(d: { key?: string }) => d.key && onSelect?.(d.key)}
            cursor={onSelect ? "pointer" : undefined}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.key} fill={d.color} fillOpacity={selected.length && !selected.includes(d.key) ? 0.3 : 1} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Two series over months on one axis (both are counts), with a legend. */
export function GroupedColumns({
  data,
  series,
  height = 220,
  onSelect,
}: {
  data: Record<string, string | number>[];
  series: { key: string; label: string; color: string }[];
  height?: number;
  /** Called with the clicked category's index. */
  onSelect?: (index: number) => void;
}) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }} barGap={2} barCategoryGap="20%">
          <CartesianGrid vertical={false} stroke="var(--line)" />
          <XAxis dataKey="label" tick={axis} axisLine={{ stroke: "var(--line)" }} tickLine={false} />
          <YAxis tick={axis} axisLine={false} tickLine={false} width={44} />
          <Tooltip {...tooltipStyle} />
          {/* Legend text stays in ink; the square beside it carries the series colour. */}
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" formatter={(v: string) => <span style={{ color: "var(--ink-2)" }}>{v}</span>} />
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
              cursor={onSelect ? "pointer" : undefined}
              onClick={(_d: unknown, i: number) => onSelect?.(i)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Donut for a part-to-whole split with a legend that names every slice. */
export function Donut({
  data,
  selected = [],
  onSelect,
  format = (n) => String(n),
}: {
  data: Datum[];
  selected?: string[];
  onSelect?: (key: string) => void;
  format?: (n: number) => string;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="flex flex-col md:flex-row items-center gap-3">
      <div style={{ width: 180, height: 180 }} className="shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Tooltip {...tooltipStyle} formatter={(v: number) => [`${format(v)} (${total ? Math.round((v / total) * 100) : 0} %)`, ""]} separator="" />
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius={52}
              outerRadius={82}
              paddingAngle={1}
              stroke="var(--card)"
              strokeWidth={2}
              onClick={(d: { key?: string }) => d.key && onSelect?.(d.key)}
              cursor={onSelect ? "pointer" : undefined}
              isAnimationActive={false}
            >
              {data.map((d) => (
                <Cell key={d.key} fill={d.color} fillOpacity={selected.length && !selected.includes(d.key) ? 0.3 : 1} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="text-[12.5px] space-y-1 w-full">
        {data.map((d) => (
          <li key={d.key}>
            <button className="flex items-center gap-2 w-full text-left hover:bg-hover rounded px-1" onClick={() => onSelect?.(d.key)}>
              <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color, opacity: selected.length && !selected.includes(d.key) ? 0.3 : 1 }} />
              <span className="flex-1 truncate">{d.label}</span>
              <span className="tnum text-ink2">{format(d.value)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Fixed-order categorical colours; a ninth category folds into "Other" upstream (SPEC §14). */
export const CATEGORICAL = Array.from({ length: 8 }, (_, i) => `var(--cat-${i + 1})`);
export const OTHER_COLOR = "var(--muted)";
